<!--
    Component TimelineDetail
    @author Leander Eyer <leander.eyer@elead.ch>
-->

<template>
	<div class="container">
		<div class="content" v-if="cursor < 0">
			<div class="bar largeText">
				<div class="gridLabel bg-timeline">{{ model.getDictionary('nav_timeline') }}</div>

				<div class="gridTimelineTriple bg-timeline-light">
					{{ timeline.getName() }}
				</div>
				<div class="link timeline-close" @click="$parent.select()">x</div>
			</div>

			<div
				class="timeline-grid-left arrow"
				v-if="offset > 0"
				@click.left="scroll(-1)"
				@mousedown.left="autoscroll('start', -1)"
				@mouseup.left="autoscroll('stop')"
			>&#x25C0;</div>
			<div
				class="timeline-grid-right arrow"
				v-if="offset < offsetMax"
				@click.left="scroll(1)"
				@mousedown.left="autoscroll('start', 1)"
				@mouseup.left="autoscroll('stop')"
			>&#x25B6;</div>

			<div class="timeline-detail-container">
				<div
					class="timeline-detail-grid"
					:style="'left: -' + offset + 'rem;'"
					@wheel.prevent="scroll($event.deltaY > 0? 1 : -1)"
				>
					<timeline-work-tile
						v-for="(entry, index) in timeline.getWorks()"
						:key="entry.work.getID()"
						:entry="entry"
						:index="index"
						:timeline="timeline"
					></timeline-work-tile>
				</div>
			</div>

			<div class="timeline-text" v-html=" textWithNewLines "></div>

			<div v-if="timeline.getFile() != null && timeline.getFile().length > 0" class="timeline-download">
				<a :href='baseURL + timeline.getFile() [0].original.link' target='_BLANK'>
					Download PDF				
				</a>				
			</div>

		</div>

		<work-detail :list="list" :index="cursor"></work-detail>
	</div>
</template>

<script>
	import Vue from "vue";
	import { linkBaseURL } from './../model/config.js'
	import { model } from "./../model/Model.js";
	import { ListWithCursor } from './../mixin/ListWithCursor.js';
	import WorkDetail from "./WorkDetail.vue";

	const TimelineWorkTile = Vue.component("timeline-work-tile", {
		props: ["entry", "index", "timeline"],
		template: `
			<div class='timeline-entry'>
				<div class='timeline-image zoomable'>
					<img 
						v-if="entry.getWork().getImages().isImage()"
						class='link'
						:src="entry.getWork().getImages().getImageURL('small')"
						:title="entry.getWork().getArtistLabel() + ' - ' + entry.getWork().getTitle() + ' (' + entry.getWork().getYear() + ')'"
						@click="$parent.select(index)">
					<div v-else
						class='link multimedia'
						v-html="entry.getWork().getImages().getMultiMediaHTML(0, 'preview')"
						:title="entry.getWork().getArtistLabel() + ' - ' + entry.getWork().getTitle() + ' (' + entry.getWork().getYear() + ')'"
						@click="$parent.select(index)"></div>
				</div>
				<div class='timeline-bar' 
					:class="entry.getFormat()" 
					:style="{ visibility: timeline.getHideYearBar()? 'hidden' : 'visible'}">{{ entry.getLabel() }}</div>
			</div>			`
	});

	export default {
		mixins: [ ListWithCursor ],
		components: { TimelineWorkTile, WorkDetail },
		props: ["timeline"],

		data: function() {
			return {
				// expose model for translations
				model: model,

				offset: 0,
				offsetMax: this.timeline.getWidth() - 66,
				baseURL: linkBaseURL,

				// autoamtic scrolling when mouse button is hold down
				autoscrollHandle: null,
				autoscrollDir: 1,
			};
		},

		computed: {
			// Prepare the Timeline Text for display by adding newlines
			textWithNewLines: function() {
				return this.timeline.getText().replace(/\n/g, "<br>");
			}
		},

		methods: {
			// change the visible slice
			scroll: function(dir) {
				if (typeof dir === "undefined") {
					dir = this.autoscrollDir;
				}
				this.offset = Math.max(
					0,
					Math.min(this.offsetMax, this.offset + dir * 2)
				);
			},

			// automatic timer based scrolling
			autoscroll: function(op, dir) {
				this.autoscrollDir = dir;
				if (this.autoscrollHandle != null) {
					clearInterval(this.autoscrollHandle);
					this.autoscrollHandle = null;
				}
				if (op == "start") {
					this.autoscrollHandle = setInterval(this.scroll, 100);
				}
			}

		},

		// on creation, fill the work list
		created: function() {
			let entries = this.timeline.getWorks();
			for (let i = 0; i < entries.length; i++) {
				this.list.push(entries [i].getWork());
			}
		}

	};
</script>


<style>

@media (min-width: 1000px) {

	/************************************************
		Card Timeline
	/************************************************/

	div.timeline-detail-container {
		overflow: hidden;
		position: relative;
		height: 13.3rem;
	}

	div.timeline-grid-left {
		position: absolute;
		top: 9rem;
		left: -3rem;
		cursor: pointer;
		user-select: none;
	}

	div.timeline-grid-right {
		position: absolute;
		top: 9rem;
		right: -3rem;
		cursor: pointer;
		user-select: none;
	}

	div.timeline-detail-grid {
		position: absolute;
		display: flex;
		flex-direction: row;
		flex-wrap: nowrap;
		justify-content: space-between;
	}

	div.timeline-image {
		height: 9rem;
	}

	div.timeline-image img {
		object-fit: cover;
		object-position: center;
		height: 9rem;
	}

	div.timeline-image div.multimedia {
		object-fit: cover;
		object-position: center;
		height: 9rem;
		width: 16rem;
	}

	div.timeline-bar {
		margin-top: 2rem;
		height: 2rem;
		padding: 0.3rem 0 0.3rem 2rem;
	}

	div.century-0,
	div.century-4,
	div.century-8 {
		background-color: #586c7e;
		color: #ffffff;
	}

	div.century-1,
	div.century-5,
	div.century-9 {
		background-color: #92a8b4;
		color: #ffffff;
	}

	div.century-2,
	div.century-6,
	div.century-10 {
		background-color: #a19f92;
		color: #ffffff;
	}

	div.century-3,
	div.century-7,
	div.century-11 {
		background-color: #c7b299;
		color: #000000;
	}

	div.timeline-text, div.timeline-download {
		margin: 2rem 10rem;
		line-height: 2rem;
	}

}

@media (max-width: 1000px) {

	/************************************************
		Card Timeline
	/************************************************/

	div.timeline-detail-container {
		overflow: hidden;
		position: relative;
		height: 13.3rem;
	}

	div.timeline-grid-left {
		position: absolute;
		top: 8rem;
		left: -2.5rem;
		padding: 1rem 2rem 1rem 0;
		cursor: pointer;
		user-select: none;
	}

	div.timeline-grid-right {
		position: absolute;
		top: 8rem;
		right: -2.5rem;
		padding: 1rem 0 1rem 2rem;
		cursor: pointer;
		user-select: none;
	}

	div.timeline-detail-grid {
		position: absolute;
		display: flex;
		flex-direction: row;
		flex-wrap: nowrap;
		justify-content: space-between;
	}

	div.timeline-image {
		height: 9rem;
	}

	div.timeline-image img {
		object-fit: cover;
		object-position: center;
		height: 9rem;
	}

	div.timeline-image div.multimedia {
		object-fit: cover;
		object-position: center;
		height: 9rem;
		width: 16rem;
	}

	div.timeline-bar {
		margin-top: 2rem;
		height: 2rem;
		padding: 0.3rem 0 0.3rem 2rem;
	}

	div.century-0,
	div.century-4,
	div.century-8 {
		background-color: #586c7e;
		color: #ffffff;
	}

	div.century-1,
	div.century-5,
	div.century-9 {
		background-color: #92a8b4;
		color: #ffffff;
	}

	div.century-2,
	div.century-6,
	div.century-10 {
		background-color: #a19f92;
		color: #ffffff;
	}

	div.century-3,
	div.century-7,
	div.century-11 {
		background-color: #c7b299;
		color: #000000;
	}

	div.timeline-text {
		margin-top: 2rem;
		line-height: 2rem;
	}

}

@media (min-width: 1000px) {

	div.timeline-close {
		display: inline;
		position: absolute;
		right: 1rem;
		top: 0rem;
	}

	div.gridTimelineTriple {
		width: 31rem;
		margin-right: 0rem;
		margin-left: 13rem;
	}

}

@media (max-width: 1000px) {

	div.timeline-close {
		position: absolute;
		top: 2.5rem;
		right: 0.5rem;
		font-size: 2rem;
	}

	div.gridTimelineTriple {
		width: 100%;
		padding: 0.4rem 0 0.4rem 2rem;
	}

}


</style>